<script lang="ts">
	import { browser } from '$app/env';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import DialogImageGallery from '$lib/components/Dialogs/DialogImageGallery.svelte';
	import WhatsAppIcon from '$lib/components/icons/WhatsApp.svelte';
	import ListingBreadcrumbs from '$lib/components/listing-detail/ListingBreadcrumbs.svelte';
	import ListingSeoSection from '$lib/components/listing-detail/ListingSeoSection.svelte';
	import ListingTypeCallout from '$lib/components/ListingTypeCallout.svelte';
	import MapComponent from '$lib/components/Map.svelte';
	import BookingPriceBreakdown from '$lib/components/pricing/BookingPriceBreakdown.svelte';
	import { buttonVariants } from '$lib/components/ui/button/index.js';
	import * as Card from '$lib/components/ui/card/index.js';
	import type { CarouselAPI } from '$lib/components/ui/carousel/context';
	import * as Carousel from '$lib/components/ui/carousel/index.js';
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import * as Popover from '$lib/components/ui/popover';
	import * as Separator from '$lib/components/ui/separator';
	import { getListingFaqs, getListingSeoIntro } from '$lib/content/listing-seo-faqs';
	import { buildListingDetailPath, type BarcosRoute } from '$lib/geo';
	import {
		getDefaultSingleDayDate,
		toSingleDayDateRange
	} from '$lib/listing-detail/default-date-range';
	import type { ListingDetailReview } from '$lib/listing-detail/review-types';
	import { getListingPresentation } from '$lib/listing-display';
	import {
		calculateListingPriceBreakdown,
		minActiveRangePrice
	} from '$lib/listing-pricing/calculate';
	import { cn } from '$lib/utils';
	import {
		buildListingBreadcrumbItems,
		buildListingHubLinks,
		buildListingImageAlt,
		buildListingMetaDescription,
		buildListingMetaTitle,
		buildListingOgImageAlt,
		buildListingStructuredDataGraph,
		geoRegionCode,
		listingRouteFromPort
	} from '$lib/utils/listing-seo';
	import ArmadorCard from '@/components/listing-detail/ArmadorCard.svelte';
	import Button from '@/components/ui/button/button.svelte';
	import RangeCalendarDay from '@/components/ui/range-calendar/range-calendar-day.svelte';
	import RangeCalendar from '@/components/ui/range-calendar/range-calendar.svelte';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import {
		BoatsKindOptions,
		BookingsStatusOptions,
		Collections,
		type BoatsResponse,
		type ListingDateRangesResponse,
		type ListingsResponse,
		type PortsResponse,
		type ReviewsResponse,
		type UsersResponse
	} from '@/pocketbase/pocketbase-types';
	import { setBookingStore } from '@/stores/BookingStore.svelte';
	import { pb } from '@/utils/ApiClient';
	import {
		CalendarDate,
		DateFormatter,
		getLocalTimeZone,
		type DateValue
	} from '@internationalized/date';
	import Anchor from '@lucide/svelte/icons/anchor';
	import Calendar from '@lucide/svelte/icons/calendar';
	import CalendarClock from '@lucide/svelte/icons/calendar-clock';
	import CalendarRange from '@lucide/svelte/icons/calendar-range';
	import ChevronsUpDown from '@lucide/svelte/icons/chevrons-up-down';
	import CircleQuestionMark from '@lucide/svelte/icons/circle-question-mark';
	import Clock from '@lucide/svelte/icons/clock';
	import Compass from '@lucide/svelte/icons/compass';
	import CreditCard from '@lucide/svelte/icons/credit-card';
	import DatabaseZap from '@lucide/svelte/icons/database-zap';
	import Flag from '@lucide/svelte/icons/flag';
	import MapPin from '@lucide/svelte/icons/map-pin';
	import Ruler from '@lucide/svelte/icons/ruler';
	import Sailboat from '@lucide/svelte/icons/sailboat';
	import Shield from '@lucide/svelte/icons/shield';
	import Ship from '@lucide/svelte/icons/ship';
	import ShipWheel from '@lucide/svelte/icons/ship-wheel';
	import Star from '@lucide/svelte/icons/star';
	import Users from '@lucide/svelte/icons/users';
	import X from '@lucide/svelte/icons/x';
	import type { DateRange } from 'bits-ui';

	// Definición de tipos correctos para evitar errores de tipo
	interface ListingWithExpand extends ListingsResponse {
		expand?: {
			boat?: BoatsWithExpand;
			user?: UsersResponse;
			cancellationPolicy?: { name: string };
		};
	}

	interface BoatsWithExpand extends BoatsResponse {
		expand?: {
			port?: PortsResponse;
			user?: UsersResponse;
		};
	}

	interface ReviewWithExpand extends ReviewsResponse {
		expand?: {
			reviewer?: UsersResponse;
		};
	}

	let {
		listing: initialListing,
		ownerReviewCount = 0,
		route: initialRoute = null,
		initialReviews = [] as ListingDetailReview[]
	}: {
		listing: ListingWithExpand;
		ownerReviewCount?: number;
		route?: BarcosRoute | null;
		initialReviews?: ListingDetailReview[];
	} = $props();

	let listing: ListingWithExpand | undefined = $state(undefined);
	const listingID: string | null = initialListing.id;
	let photosList: string[] = $state([]);
	let listingOwner: UsersResponse | undefined = $state(undefined);
	let selectedPhoto: string = $state('');
	let boat: BoatsWithExpand | undefined = $state(undefined);
	let selectedCrewNumber: number = $state(1);
	let chooseCreNumberOpen: boolean = $state(false);
	let numberOfDays: number = $state(0);
	let port: PortsResponse = $state({} as PortsResponse);
	let listingDateRanges: ListingDateRangesResponse[] = $state([]);
	let availableDateRanges: { start: Date; end: Date }[] = $state([]);
	let valueDate: DateRange = $state({
		start: undefined,
		end: undefined
	});
	let loadingAvailableDates: boolean = $state(true);
	let reviews: ReviewWithExpand[] = $state(initialReviews as ReviewWithExpand[]);
	let loadingReviews: boolean = $state(initialReviews.length === 0);
	let averageRating: number = $state(
		initialReviews.length > 0
			? initialReviews.reduce((sum, review) => sum + review.rating, 0) / initialReviews.length
			: 0
	);

	// Variables para el diálogo de imagen completa
	let imageDialogOpen: boolean = $state(false);
	let initialImageIndex: number = $state(0);

	// Variables para el carrusel
	let emblaApi: CarouselAPI | undefined = $state(undefined);
	let emblaSelectedIndex: number = $state(0);

	// Estado para almacenar la orientación de cada imagen
	let imageOrientations: Map<string, 'portrait' | 'landscape' | 'square'> = $state(new Map());

	// Variables para el diálogo de fotos de reseñas
	let reviewPhotosDialogOpen: boolean = $state(false);
	let currentReviewPhotos: string[] = $state([]);
	let currentReview: ReviewsResponse | undefined = $state(undefined);
	let reviewCarouselApi: CarouselAPI | undefined = $state(undefined);
	let reviewCarouselSelectedIndex: number = $state(0);
	let initialPhotoIndex: number = $state(0);

	function emblaScrollTo(index: number) {
		emblaApi?.scrollTo(index);
	}

	// Función para detectar la orientación de una imagen
	function detectImageOrientation(event: Event, photoId: string) {
		const img = event.target as HTMLImageElement;
		const aspectRatio = img.naturalWidth / img.naturalHeight;

		let orientation: 'portrait' | 'landscape' | 'square';
		if (aspectRatio < 0.95) {
			orientation = 'portrait';
		} else if (aspectRatio > 1.05) {
			orientation = 'landscape';
		} else {
			orientation = 'square';
		}

		imageOrientations.set(photoId, orientation);
		// Forzar reactividad
		imageOrientations = new Map(imageOrientations);
	}

	function setEmblaApi(api: CarouselAPI | undefined) {
		emblaApi = api;
		if (api) {
			emblaSelectedIndex = api.selectedScrollSnap();
			api.on('select', () => {
				emblaSelectedIndex = api.selectedScrollSnap();
			});
		}
	}

	function setReviewCarouselApi(api: CarouselAPI | undefined) {
		reviewCarouselApi = api;
		if (api) {
			reviewCarouselSelectedIndex = api.selectedScrollSnap();
			api.on('select', () => {
				reviewCarouselSelectedIndex = api.selectedScrollSnap();
			});
			// Si hay un índice inicial pendiente, hacer scroll
			if (initialPhotoIndex > 0) {
				api.scrollTo(initialPhotoIndex);
				initialPhotoIndex = 0;
			}
		}
	}

	function openReviewPhotosDialog(review: ReviewWithExpand, photoIndex: number = 0) {
		if (review.photos && review.photos.length > 0) {
			currentReviewPhotos = review.photos as string[];
			currentReview = review;
			initialPhotoIndex = photoIndex;
			reviewPhotosDialogOpen = true;
			// Si el API ya está disponible, hacer scroll inmediatamente
			if (reviewCarouselApi) {
				reviewCarouselApi.scrollTo(photoIndex);
				initialPhotoIndex = 0;
			}
		}
	}

	function openImageDialog(photo: string, index: number = 0) {
		if (boat) {
			initialImageIndex = index;
			imageDialogOpen = true;
		}
	}

	$effect(() => {
		numberOfDays = calculateBookingDuration(valueDate);
	});

	let isFavourite: boolean = $derived.by(() => {
		return listing?.id ? isListingFavourite(listing.id) : false;
	});

	const whatsappUrl = $derived.by(() => {
		if (!browser || !listing?.id) {
			return 'https://wa.me/34679855762';
		}
		const listingUrl = page.url.href;
		const message = `Hola, estoy interesado en ${listingUrl} pero tengo dudas`;
		const encodedMessage = encodeURIComponent(message);
		return `https://wa.me/34679855762?text=${encodedMessage}`;
	});

	const detailPath = $derived(
		buildListingDetailPath(initialListing, initialListing.expand?.boat?.expand?.port ?? null)
	);
	const canonicalUrl = $derived(`https://zarpar.eu${localizeHref(detailPath)}`);
	const hreflangUrl = (locale: 'es' | 'gl' | 'en') =>
		`https://zarpar.eu${localizeHref(detailPath, { locale })}`;

	function calculateBookingDuration(value: DateRange): number {
		let days: number = 0;
		if (!value?.end) {
			days = 1;
		} else {
			days =
				(Number(value?.end?.toDate(getLocalTimeZone())) -
					Number(value?.start?.toDate(getLocalTimeZone()))) /
					(1000 * 60 * 60 * 24) +
				1;
		}
		return days;
	}

	function dateValueToIso(date: DateValue): string {
		const d = date.toDate(getLocalTimeZone());
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	const activeDateRanges = $derived(listingDateRanges.filter((r) => r.active !== false));
	const minListingPrice = $derived(minActiveRangePrice(activeDateRanges));

	const seoBoat = $derived(initialListing.expand?.boat);

	const effectiveRoute = $derived(
		initialRoute ?? listingRouteFromPort(initialListing, seoBoat?.expand?.port)
	);

	const seoTitle = $derived(
		seoBoat ? buildListingMetaTitle(initialListing, seoBoat, true) : 'Salida en barco | Zarpar'
	);

	const seoDescription = $derived(
		seoBoat
			? buildListingMetaDescription({
					listing: initialListing,
					boat: seoBoat,
					minPrice: minListingPrice,
					listingTypeLabel: getListingPresentation(initialListing).label,
					isExperience: true
				})
			: 'Únete a esta salida en barco en Zarpar. Reserva online y vive una experiencia náutica única.'
	);

	const seoOgImageAlt = $derived(
		seoBoat ? buildListingOgImageAlt(initialListing, seoBoat, true) : 'Salida en barco'
	);

	const breadcrumbItems = $derived(
		buildListingBreadcrumbItems({
			route: effectiveRoute,
			listingTitle: initialListing.title,
			port: seoBoat?.expand?.port,
			listingKind: initialListing.kind
		})
	);

	const seoHubLinks = $derived(buildListingHubLinks(effectiveRoute, seoBoat?.expand?.port));

	const seoFaqs = $derived(
		seoBoat
			? getListingFaqs({
					kind: initialListing.kind,
					capitanMode: initialListing.capitanMode,
					boatKind: seoBoat.kind,
					port: seoBoat.expand?.port,
					boatLength: seoBoat.length,
					spots: initialListing.spots,
					minPrice: minListingPrice
				})
			: []
	);

	const seoIntro = $derived(
		seoBoat
			? getListingSeoIntro(
					{
						kind: initialListing.kind,
						capitanMode: initialListing.capitanMode,
						boatKind: seoBoat.kind,
						port: seoBoat.expand?.port,
						boatLength: seoBoat.length,
						spots: initialListing.spots,
						minPrice: minListingPrice
					},
					seoHubLinks
				)
			: { heading: '', paragraphs: [], hubLinks: [] }
	);

	const structuredData = $derived.by(() => {
		if (!seoBoat) return null;

		const imageUrls =
			photosList.length > 0
				? photosList.map((photo) => pb.files.getURL(seoBoat, photo))
				: undefined;

		return buildListingStructuredDataGraph({
			breadcrumbItems,
			pageUrl: canonicalUrl,
			listing: initialListing,
			boat: seoBoat,
			minPrice: minListingPrice,
			imageUrls,
			sellerName: listingOwner?.username,
			inStock: availableDateRanges.length > 0,
			reviews,
			faqs: seoFaqs,
			isExperience: true
		});
	});

	const jsonLd = $derived(structuredData ? JSON.stringify(structuredData) : null);

	const geoRegionMeta = $derived(
		seoBoat?.expand?.port?.province ? geoRegionCode(seoBoat.expand.port.province) : null
	);

	function listingPhotoAlt(photoIndex: number): string {
		return buildListingImageAlt({
			listingTitle: listing?.title,
			boatKind: boat?.kind,
			port: boat?.expand?.port,
			photoIndex,
			isExperience: true
		});
	}

	const priceBreakdownResult = $derived.by(() => {
		if (!listing || !valueDate?.start || !valueDate?.end) return null;
		return calculateListingPriceBreakdown({
			listing,
			dateRanges: activeDateRanges,
			startDate: dateValueToIso(valueDate.start),
			endDate: dateValueToIso(valueDate.end),
			passengers: selectedCrewNumber
		});
	});

	function getDateRangePriceForDay(date: DateValue): number | null {
		const checkDate = date.toDate(getLocalTimeZone());
		checkDate.setHours(0, 0, 0, 0);

		const matchingRange = activeDateRanges.find((range) => {
			const startDate = new Date(range.startDate);
			const endDate = new Date(range.endDate);
			startDate.setHours(0, 0, 0, 0);
			endDate.setHours(23, 59, 59, 999);

			return checkDate >= startDate && checkDate <= endDate;
		});

		return matchingRange?.price ?? null;
	}

	// Inicialización síncrona desde el prop para que el SSR/prerender incluya
	// el contenido del anuncio en el HTML.
	applyListing(initialListing);

	$effect(() => {
		// Datos secundarios: solo en cliente (los $effect no corren en SSR).
		getAvailableDates();
		if (initialReviews.length === 0) {
			getReviews();
		}
	});

	function applyListing(result: ListingWithExpand) {
		listing = result;
		if (result.expand) {
			listingOwner = result.expand.user;

			if (result.expand.boat) {
				boat = result.expand.boat;

				if (boat.expand?.port) {
					port = boat.expand.port;
				}
			}
		}

		// Usar las fotos del listing en lugar de las del barco
		if (result.photos) {
			photosList = result.photos as string[];
			if (photosList.length > 0) {
				selectedPhoto = photosList[0];
			}
		}
	}

	async function getAvailableDates() {
		if (!listingID) return;

		loadingAvailableDates = true;
		try {
			const dateRanges = await pb
				.collection(Collections.ListingDateRanges)
				.getFullList<ListingDateRangesResponse>({
					filter: `listing = "${listingID}" && active = true`
				});

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			listingDateRanges = dateRanges;

			availableDateRanges = dateRanges
				.map((range) => {
					const startDate = new Date(range.startDate);
					const endDate = new Date(range.endDate);

					// Ajustar la fecha de inicio si es anterior a hoy
					const adjustedStartDate = startDate < today ? today : startDate;

					// Solo incluir el rango si la fecha de fin es hoy o posterior
					if (endDate >= today) {
						return {
							start: adjustedStartDate,
							end: endDate
						};
					}
					return null;
				})
				.filter((range): range is { start: Date; end: Date } => range !== null);

			const defaultDate = getDefaultSingleDayDate(availableDateRanges, today);
			if (defaultDate) {
				valueDate = toSingleDayDateRange(defaultDate);
			}
		} catch (error) {
			console.error('Error al cargar fechas disponibles:', error);
		} finally {
			loadingAvailableDates = false;
		}
	}

	async function getReviews() {
		if (!listingID) return;

		loadingReviews = true;
		try {
			const reviewsList = await pb.collection(Collections.Reviews).getFullList<ReviewWithExpand>({
				filter: `listing = "${listingID}"`,
				expand: 'reviewer',
				sort: '-created'
			});

			reviews = reviewsList;

			// Calcular promedio de rating
			if (reviews.length > 0) {
				const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
				averageRating = totalRating / reviews.length;
			} else {
				averageRating = 0;
			}
		} catch (error) {
			console.error('Error al cargar reseñas:', error);
			reviews = [];
			averageRating = 0;
		} finally {
			loadingReviews = false;
		}
	}

	// Función para verificar si una fecha está disponible
	function isDateAvailable(date: DateValue): boolean {
		if (availableDateRanges.length === 0) {
			return false; // Si no hay fechas disponibles, todas están deshabilitadas
		}

		const checkDate = date.toDate(getLocalTimeZone());
		// Normalizar la fecha para comparar solo año, mes y día
		checkDate.setHours(0, 0, 0, 0);

		return availableDateRanges.some((range) => {
			const startDate = new Date(range.start);
			const endDate = new Date(range.end);
			startDate.setHours(0, 0, 0, 0);
			endDate.setHours(23, 59, 59, 999);

			return checkDate >= startDate && checkDate <= endDate;
		});
	}

	// Función para deshabilitar fechas no disponibles
	function isDateDisabled(date: DateValue): boolean {
		// Solo deshabilitar fechas no disponibles
		// (las fechas pasadas ya están filtradas en getAvailableDates)
		return !isDateAvailable(date);
	}

	async function handleBookingCreation() {
		if (!listing || !boat || !listingOwner) {
			console.error('Faltan datos necesarios para crear la reserva');
			return;
		}

		// Validar que se hayan seleccionado fechas
		if (!valueDate?.start || !valueDate?.end) {
			alert(
				'Por favor selecciona las fechas de tu reserva. Para reservar un solo día, haz clic dos veces en la misma fecha.'
			);
			return;
		}

		// Validar que las fechas seleccionadas estén disponibles
		const startDate = valueDate.start.toDate(getLocalTimeZone());
		const endDate = valueDate.end.toDate(getLocalTimeZone());

		// Verificar que todas las fechas en el rango estén disponibles
		let currentDate = new Date(startDate);
		while (currentDate <= endDate) {
			const calendarDate = new CalendarDate(
				currentDate.getFullYear(),
				currentDate.getMonth() + 1,
				currentDate.getDate()
			);

			if (!isDateAvailable(calendarDate)) {
				alert('Las fechas seleccionadas no están disponibles. Por favor selecciona otras fechas.');
				return;
			}

			currentDate.setDate(currentDate.getDate() + 1);
		}

		const breakdown = priceBreakdownResult;
		if (!breakdown) {
			alert(
				'No se pudo calcular el precio para las fechas seleccionadas. Por favor selecciona otras fechas.'
			);
			return;
		}

		const startDateIso = dateValueToIso(valueDate.start);
		const endDateIso = dateValueToIso(valueDate.end);

		const bookingData = {
			listing: listing.id,
			// user se asignará en la página de confirmación después de autenticación
			boat: boat.id,
			startDate: startDateIso,
			endDate: endDateIso,
			numberOfDays: numberOfDays,
			passangers: selectedCrewNumber,
			price: breakdown.total,
			totalPrice: breakdown.total,
			priceBreakdown: breakdown.lines,
			status: BookingsStatusOptions.pending,
			expand: {
				listing: listing,
				boat: boat,
				owner: listingOwner
			}
		};

		setBookingStore(bookingData as any);
		goto(localizeHref('/experiencia/confirmacion'));
	}

	const df = new DateFormatter('es-ES', {
		dateStyle: 'medium'
	});

	$inspect('valueDate', valueDate);

	let startValue: DateValue | undefined = $state(undefined);

	// Stub para la función que falta (para evitar el error de linting)
	function isListingFavourite(id: string): boolean {
		// TODO: Implementar lógica de favoritos
		return false;
	}
</script>

<svelte:head>
	<link rel="preconnect" href="https://maps.googleapis.com" />
	<link rel="preconnect" href="https://maps.gstatic.com" />
	<title>{seoTitle}</title>
	<meta name="description" content={seoDescription} />

	<link rel="canonical" href={canonicalUrl} />

	<link rel="alternate" hreflang="es" href={hreflangUrl('es')} />
	<link rel="alternate" hreflang="gl" href={hreflangUrl('gl')} />
	<link rel="alternate" hreflang="en" href={hreflangUrl('en')} />
	<link rel="alternate" hreflang="x-default" href={hreflangUrl('es')} />

	<meta property="og:title" content={seoTitle} />
	<meta property="og:description" content={seoDescription} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content={canonicalUrl} />
	{#if boat && photosList.length > 0}
		<meta property="og:image" content={pb.files.getURL(boat, photosList[0])} />
		<meta property="og:image:alt" content={seoOgImageAlt} />
	{/if}
	<meta property="og:site_name" content="Zarpar" />
	<meta property="og:locale" content={getLocale()} />

	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={seoTitle} />
	<meta name="twitter:description" content={seoDescription} />
	{#if boat && photosList.length > 0}
		<meta name="twitter:image" content={pb.files.getURL(boat, photosList[0])} />
		<meta name="twitter:image:alt" content={seoOgImageAlt} />
	{/if}

	<meta name="robots" content="index, follow" />
	<meta name="author" content={listingOwner?.username || 'Zarpar'} />
	{#if minListingPrice}
		<meta name="product:price:amount" content={minListingPrice.toString()} />
		<meta name="product:price:currency" content="EUR" />
	{/if}
	{#if boat?.expand?.port}
		{#if geoRegionMeta}
			<meta name="geo.region" content={`ES-${geoRegionMeta}`} />
		{/if}
		<meta name="geo.placename" content={boat.expand.port.city ?? boat.expand.port.name} />
	{/if}

	{#if jsonLd}
		{@html '<script type="application/ld+json">' + jsonLd + '</scr' + 'ipt>'}
	{/if}
</svelte:head>

{#if listing && photosList.length > 0}
	<div class="mx-auto max-w-7xl xl:px-8 2xl:px-0">
		<ListingBreadcrumbs items={breadcrumbItems} />

		<div class="flex items-center justify-between gap-4 px-2 md:px-0">
			<h1 class="title-font pt-4 pb-2 text-2xl font-medium text-gray-900 md:pt-6 md:text-4xl">
				{listing.title}
			</h1>
		</div>
		<div class="flex flex-col lg:gap-x-8">
			<!-- Galería de imágenes -->
			<Carousel.Root class="w-full" setApi={setEmblaApi}>
				<Carousel.Content>
					{#each photosList as photo, i (photo)}
						{@const orientation = imageOrientations.get(photo)}
						<Carousel.Item
							class={cn(
								'',
								orientation === 'portrait'
									? 'md:basis-72 lg:basis-80'
									: 'md:basis-auto lg:basis-auto'
							)}
						>
							<div class="relative">
								<Card.Root class="overflow-hidden rounded-none p-0 md:rounded-lg">
									<Card.Content class="p-0">
										{#if boat}
											<div
												class={cn(
													'cursor-pointer overflow-hidden transition-all',
													// En móvil siempre aspect-square, en desktop varía según orientación
													'aspect-square',
													orientation === 'portrait'
														? 'md:flex md:aspect-auto md:h-96 md:items-center md:justify-center md:bg-linear-to-b md:from-gray-50/50 md:to-gray-100/50'
														: 'md:aspect-[4/3] md:h-96'
												)}
											>
												<img
													src={pb.files.getURL(boat, photo, {
														thumb: '0x450'
													})}
													alt={listingPhotoAlt(i + 1)}
													height="450"
													loading={i < 2 ? 'eager' : 'lazy'}
													fetchpriority={i < 2 ? 'high' : 'low'}
													class={cn(
														'transition-transform hover:scale-105 md:rounded-lg',
														// En móvil siempre object-cover, en desktop varía según orientación
														'h-full w-full object-cover',
														orientation === 'portrait'
															? 'md:h-full md:w-auto md:max-w-full md:object-contain'
															: ''
													)}
													onload={(e) => detectImageOrientation(e, photo)}
													onclick={() => openImageDialog(photo, i)}
												/>
											</div>
										{/if}
									</Card.Content>
								</Card.Root>
							</div>
						</Carousel.Item>
					{/each}
				</Carousel.Content>
				<Carousel.Previous class="absolute top-1/2 left-4 z-10 hidden -translate-y-1/2 md:flex" />
				<Carousel.Next class="absolute top-1/2 right-4 z-10 hidden -translate-y-1/2 md:flex" />

				<!-- Puntos indicadores para móvil -->
				<div class="mt-2 flex justify-center gap-1 md:hidden">
					{#each photosList as _, i}
						{#if emblaApi}
							<button
								class={`h-2 w-2 rounded-full transition-colors ${
									emblaSelectedIndex === i ? 'bg-kbprimary' : 'bg-gray-300'
								}`}
								aria-label={`Ir a la diapositiva ${i + 1}`}
								onclick={() => emblaScrollTo(i)}
							></button>
						{:else}
							<div class={`h-2 w-2 rounded-full ${i === 0 ? 'bg-kbprimary' : 'bg-gray-300'}`}></div>
						{/if}
					{/each}
				</div>
			</Carousel.Root>

			<!-- Diálogo para imagen a tamaño completo -->
			{#if boat}
				<DialogImageGallery
					bind:open={imageDialogOpen}
					photosList={photosList}
					record={boat}
					initialIndex={initialImageIndex}
					alt={listingPhotoAlt(initialImageIndex + 1)}
				/>
			{/if}

			<!-- Diálogo para fotos de reseñas con carrusel -->
			<Dialog.Root bind:open={reviewPhotosDialogOpen}>
				<Dialog.Content
					class="max-h-[95vh] w-[95vw] overflow-hidden p-0 sm:max-w-[95vw] md:w-[95vw] lg:w-[98vw] lg:max-w-[98vw] [&_button]:text-white [&_button_svg]:text-white"
				>
					{#if currentReview && currentReviewPhotos.length > 0}
						<div class="relative w-full">
							<Carousel.Root class="w-full" setApi={setReviewCarouselApi}>
								<Carousel.Content>
									{#each currentReviewPhotos as photo (photo)}
										<Carousel.Item class="basis-full">
											<div
												class="relative flex h-[85vh] items-center justify-center bg-black/90 p-2 md:h-[90vh] md:p-4"
											>
												<img
													src={pb.files.getURL(currentReview, photo, { thumb: '0x900' })}
													alt="Foto de la reseña"
													class="max-h-full max-w-full object-contain"
												/>
											</div>
										</Carousel.Item>
									{/each}
								</Carousel.Content>
								{#if currentReviewPhotos.length > 1}
									<Carousel.Previous
										class="absolute top-1/2 left-2 z-10 h-10 w-10 -translate-y-1/2 bg-black/60 text-white transition-all hover:bg-black/80 active:scale-95 md:left-4 md:h-12 md:w-12"
									/>
									<Carousel.Next
										class="absolute top-1/2 right-2 z-10 h-10 w-10 -translate-y-1/2 bg-black/60 text-white transition-all hover:bg-black/80 active:scale-95 md:right-4 md:h-12 md:w-12"
									/>
									<!-- Contador de imágenes -->
									<div
										class="absolute bottom-12 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1.5 text-xs text-white md:bottom-4 md:px-4 md:py-2 md:text-sm"
									>
										{reviewCarouselSelectedIndex + 1} / {currentReviewPhotos.length}
									</div>
									<!-- Puntos indicadores para móvil -->
									<div class="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5 md:hidden">
										{#each currentReviewPhotos as _, i (i)}
											<button
												type="button"
												class={`h-2 w-2 rounded-full transition-colors ${
													reviewCarouselSelectedIndex === i ? 'bg-white' : 'bg-white/50'
												}`}
												aria-label={`Ir a la imagen ${i + 1}`}
												onclick={() => reviewCarouselApi?.scrollTo(i)}
											></button>
										{/each}
									</div>
								{/if}
							</Carousel.Root>
						</div>
					{/if}
				</Dialog.Content>
			</Dialog.Root>

			<div class="px-2 md:px-0">
				<!-- Información del producto -->
				<div class="flex flex-col md:grid md:grid-cols-12 md:gap-10">
					<!-- Columna principal con detalles -->
					<div class="col-span-full md:col-span-8">
						<!-- Cabecera con tipo de barco y ubicación -->
						<div class="py-6">
							{#if listing}
								<div class="mx-2 mb-4">
									<ListingTypeCallout listing={listing} boatKind={boat?.kind} />
								</div>
							{/if}

							<div class="mx-2">
								<!-- Ubicación y Rating -->
								<div class="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
									<h2 class="text-md flex items-center text-gray-700">
										<MapPin class="text-kbblue mr-2 inline-flex size-5" />
										{boat?.expand?.port?.name}, {boat?.expand?.port?.province}
									</h2>
									{#if reviews.length > 0}
										<a
											href="#reviews"
											class="group flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-base text-gray-700 shadow-sm transition-all active:scale-95 md:border-0 md:bg-transparent md:shadow-none md:hover:bg-gray-100 md:hover:text-gray-900 md:hover:underline"
										>
											<Star
												class="h-5 w-5 fill-yellow-400 text-yellow-400 transition-transform group-hover:scale-110"
											/>
											<span class="text-lg font-semibold">{averageRating.toFixed(1)}</span>
											<span class="text-gray-500"
												>({reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'})</span
											>
											<svg
												class="ml-1 h-4 w-4 text-gray-400 md:hidden"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M9 5l7 7-7 7"
												></path>
											</svg>
										</a>
									{/if}
								</div>

								<!-- Capacidad -->
								<p class="text-md mt-2 flex items-center text-gray-700">
									<Users class="text-kbblue mr-2 inline-flex size-5" />
									Hasta {listing?.spots || 0}
									{#if listing?.spots === 1}
										Tripulante
									{:else}
										Tripulantes
									{/if}
								</p>
							</div>
						</div>

						<!-- Panel de precios para móvil -->
						<div class="mb-6 md:hidden">
							<Card.Root class="border-2">
								<Card.Header class="-mb-4">
									<div class="flex items-baseline justify-between">
										<Card.Title class="text-kbprimary text-3xl font-bold">
											{minListingPrice ?? '—'}€
											<span class="text-sm font-normal text-gray-500">
												{#if listing?.pricingType === 'per_seat'}
													/ persona / día
												{:else}
													/ día
												{/if}
											</span>
										</Card.Title>
										{#if reviews.length > 0}
											<a
												href="#reviews"
												class="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
											>
												<Star class="h-4 w-4 fill-yellow-400 text-yellow-400" />
												<span class="font-semibold">{averageRating.toFixed(1)}</span>
												<span class="text-gray-500">({reviews.length})</span>
											</a>
										{/if}
									</div>
								</Card.Header>

								<Card.Content class="space-y-3">
									<!-- Selector de fechas -->
									<div>
										<label class="text-sm font-medium text-gray-700">Selecciona fechas</label>
										{#if loadingAvailableDates}
											<p class="mt-1 text-xs text-gray-500">Cargando fechas disponibles...</p>
										{:else if availableDateRanges.length === 0}
											<p class="mt-1 text-xs text-red-500">
												No hay fechas disponibles para este barco
											</p>
										{:else}
											<p class="mt-1 text-xs text-gray-500">
												Solo se pueden seleccionar fechas disponibles
											</p>
										{/if}
										<div class="mt-1">
											<Popover.Root>
												<Popover.Trigger class="w-full">
													<div
														class={cn(
															buttonVariants({ variant: 'outline' }),
															'w-full justify-start border-slate-300 bg-white text-left text-sm font-normal hover:bg-slate-50'
														)}
													>
														<Calendar class="text-kbblue mr-2 h-4 w-4" />
														{#if valueDate && valueDate.start}
															{#if valueDate.end}
																{df.format(valueDate.start.toDate(getLocalTimeZone()))} - {df.format(
																	valueDate.end.toDate(getLocalTimeZone())
																)}
															{:else}
																{df.format(valueDate.start.toDate(getLocalTimeZone()))}
															{/if}
														{:else}
															Selecciona fechas
														{/if}
													</div>
												</Popover.Trigger>
												<Popover.Content class="w-auto p-0" align="center">
													<p class="text-xs text-slate-600 text-center mt-2">
														💡 Para un día, haz clic 2 veces en la misma fecha
													</p>
													<RangeCalendar
														class="rounded-lg border [--cell-size:--spacing(11)] md:[--cell-size:--spacing(12)]"
														bind:value={valueDate}
														onStartValueChange={(v) => {
															startValue = v;
														}}
														numberOfMonths={1}
														weekStartsOn={1}
														placeholder={valueDate?.start}
														isDateDisabled={isDateDisabled}
														minDays={1}
													>
														{#snippet day({ day, outsideMonth })}
															<RangeCalendarDay
																class="flex h-(--cell-size) flex-col items-center justify-center gap-0 py-0.5 leading-none"
															>
																<span class="text-sm">{day.day}</span>
																{#if !outsideMonth && isDateAvailable(day) && getDateRangePriceForDay(day) !== null}
																	<span class="text-[9px] leading-none text-slate-500"
																		>{getDateRangePriceForDay(day) ?? 0}€</span
																	>
																{/if}
															</RangeCalendarDay>
														{/snippet}
													</RangeCalendar>
												</Popover.Content>
											</Popover.Root>
										</div>
									</div>

									<!-- Selector de tripulantes -->
									<div>
										<label class="text-sm font-medium text-gray-700">Número de tripulantes</label>
										<div class="relative mt-1">
											<button
												onclick={() => (chooseCreNumberOpen = !chooseCreNumberOpen)}
												type="button"
												class="focus:ring-kbblue flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm shadow-xs hover:bg-slate-50 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
											>
												<div class="flex items-center">
													<Users class="text-kbblue mr-2 h-4 w-4" />
													{selectedCrewNumber}
													{selectedCrewNumber === 1 ? 'Tripulante' : 'Tripulantes'}
												</div>
												<ChevronsUpDown class="h-4 w-4 text-gray-400" />
											</button>

											{#if chooseCreNumberOpen}
												<div
													class="ring-opacity-5 absolute right-0 z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-hidden"
												>
													{#each Array(listing?.spots || 10) as _, i (i)}
														<button
															onclick={() => {
																selectedCrewNumber = i + 1;
																chooseCreNumberOpen = false;
															}}
															class={`block w-full px-4 py-2 text-left text-sm ${selectedCrewNumber === i + 1 ? 'bg-kbblue/10 text-kbblue' : 'text-gray-700 hover:bg-gray-100'}`}
														>
															{i + 1}
															{i + 1 === 1 ? 'Tripulante' : 'Tripulantes'}
														</button>
													{/each}
												</div>
											{/if}
										</div>
									</div>

									<Separator.Root />

									<!-- Resumen de costos -->
									<div class="rounded-lg bg-slate-50 p-3">
										<h4 class="mb-3 font-medium">Resumen del precio</h4>
										{#if priceBreakdownResult}
											<BookingPriceBreakdown
												lines={priceBreakdownResult.lines}
												total={priceBreakdownResult.total}
												compact
											/>
										{:else}
											<p class="text-sm text-slate-600">
												Selecciona fechas disponibles para ver el precio.
											</p>
										{/if}
									</div>

									<!-- Botón de reserva -->
									<Button
										class="w-full py-2.5 text-sm font-medium"
										onclick={handleBookingCreation}
										disabled={loadingAvailableDates || availableDateRanges.length === 0}
									>
										<CreditCard class="mr-2 h-4 w-4 shrink-0" />
										<span class="truncate">
											{#if loadingAvailableDates}
												Cargando...
											{:else if availableDateRanges.length === 0}
												No disponible
											{:else}
												Reservar ahora
											{/if}
										</span>
									</Button>

									<a
										class={cn(
											buttonVariants({ variant: 'default' }),
											'mt-2 flex w-full items-center justify-center gap-2 bg-[#25D366] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1DA851] active:bg-[#1A9D47]'
										)}
										href={whatsappUrl}
										target="_blank"
										rel="noreferrer"
									>
										<WhatsAppIcon class="h-4 w-4 shrink-0" size={16} />
										<span class="truncate text-center">¿No lo tienes claro? Escribenos!</span>
									</a>

									<!-- Garantías -->
									<div class="rounded-lg bg-gray-100 p-3 text-xs text-gray-600">
										<div class="flex items-center justify-center gap-2">
											<Shield class="text-kbblue h-4 w-4" />
											<p>Pago seguro</p>
										</div>
										<div class="mt-1 flex items-center justify-center gap-2">
											<Calendar class="text-kbblue h-4 w-4" />
											<p>Cancelación flexible disponible</p>
										</div>
									</div>
								</Card.Content>
							</Card.Root>
						</div>

						<!-- Armador -->
						{#if listingOwner}
							<ArmadorCard owner={listingOwner} reviewCount={ownerReviewCount} />
						{/if}

						<!-- Descripción -->
						<div class="mb-6 rounded-lg border p-5">
							<h3 class="mb-3 text-lg font-semibold text-gray-900">Descripción</h3>
							<p class="whitespace-pre-wrap text-gray-700">{listing?.description}</p>
						</div>

						<!-- Características del barco -->
						<Card.Root class="mb-6">
							<Card.Header class="pb-2">
								<Card.Title class="flex items-center">
									<Ship class="text-kbblue mr-2 h-5 w-5" />
									Características del barco
								</Card.Title>
							</Card.Header>
							<Card.Content>
								<div class="grid grid-cols-2 gap-4 md:grid-cols-3">
									<!-- Tipo -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											{#if boat?.kind === BoatsKindOptions.motorboat}
												<Ship class="text-kbblue h-5 w-5" />
											{:else}
												<Sailboat class="text-kbblue h-5 w-5" />
											{/if}
										</div>
										<div>
											<p class="text-sm font-medium">Tipo</p>
											<p class="text-sm text-gray-600">
												{boat?.kind === BoatsKindOptions.motorboat ? 'Motora' : 'Velero'}
											</p>
										</div>
									</div>

									<!-- Tamaño -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<Ruler class="text-kbblue h-5 w-5" />
										</div>
										<div>
											<p class="text-sm font-medium">Tamaño</p>
											<p class="text-sm text-gray-600">{boat?.length || '--'} metros</p>
										</div>
									</div>

									<!-- Capacidad -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<Users class="text-kbblue h-5 w-5" />
										</div>
										<div>
											<p class="text-sm font-medium">Capacidad</p>
											<p class="text-sm text-gray-600">
												{listing?.spots || '--'}
												{listing?.spots === 1 ? 'persona' : 'personas'}
											</p>
										</div>
									</div>

									<!-- Marca -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<Anchor class="text-kbblue h-5 w-5" />
										</div>
										<div>
											<p class="text-sm font-medium">Marca</p>
											<p class="text-sm text-gray-600">{boat?.builder || 'No especificada'}</p>
										</div>
									</div>

									<!-- Modelo -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<Sailboat class="text-kbblue h-5 w-5" />
										</div>
										<div>
											<p class="text-sm font-medium">Modelo</p>
											<p class="text-sm text-gray-600">{boat?.model || 'No especificado'}</p>
										</div>
									</div>

									<!-- Año -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<CalendarRange class="text-kbblue h-5 w-5" />
										</div>
										<div>
											<p class="text-sm font-medium">Año</p>
											<p class="text-sm text-gray-600">{boat?.constructionYear || '--'}</p>
										</div>
									</div>

									<!-- Potencia -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<DatabaseZap class="text-kbblue h-5 w-5" />
										</div>
										<div>
											<p class="text-sm font-medium">Potencia</p>
											<p class="text-sm text-gray-600">{boat?.horsepower || '--'} CV</p>
										</div>
									</div>
								</div>
							</Card.Content>
						</Card.Root>

						<!-- Detalles del alquiler -->
						<Card.Root class="mb-6">
							<Card.Header class="pb-2">
								<Card.Title class="flex items-center">
									<CalendarClock class="text-kbblue mr-2 h-5 w-5" />
									Detalles de la experiencia
								</Card.Title>
							</Card.Header>
							<Card.Content>
								<div class="grid gap-4 md:grid-cols-2">
									<!-- Horarios -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<Clock class="text-kbblue h-5 w-5" />
										</div>
										<div class="flex-1">
											<p class="text-sm font-medium">Check-in / Check-out</p>
											<p class="whitespace-pre-line text-sm text-gray-600">
												{listing?.meetingDetails}
											</p>
										</div>
									</div>

									<!-- Política de cancelación -->
									<div class="flex items-start space-x-2">
										<div class="rounded-md bg-slate-100 p-2">
											<X class="text-kbblue h-5 w-5" />
										</div>
										<div class="flex-1">
											<p class="text-sm font-medium">Política de cancelación</p>
											<p class="text-sm text-gray-600">
												{#if listing?.expand?.cancellationPolicy?.name === 'strict'}
													Estricta (15 días)
												{:else}
													Flexible (2 días)
												{/if}
												<Popover.Root>
													<Popover.Trigger>
														<CircleQuestionMark class="text-kbblue ml-1 inline-flex size-4" />
													</Popover.Trigger>
													<Popover.Content class="w-80" side="top">
														<div class="grid gap-2">
															<p>
																<span class="font-bold">Flexible:</span> Devolución del 100% hasta 2 días
																antes.
															</p>
															<p>
																<span class="font-bold">Estricta:</span> Devolución del 100% hasta 15
																días antes.
															</p>
														</div>
													</Popover.Content>
												</Popover.Root>
											</p>
										</div>
									</div>
								</div>
							</Card.Content>
						</Card.Root>

						<!-- Localización -->
						<Card.Root class="mb-6">
							<Card.Header class="pb-2">
								<Card.Title class="flex items-center">
									<MapPin class="text-kbblue mr-2 h-5 w-5" />
									Localización del puerto
								</Card.Title>
							</Card.Header>
							<Card.Content>
								<div class="h-80 w-full overflow-hidden rounded-lg">
									{#if port}
										<MapComponent port={port} />
									{:else}
										<div class="flex h-full w-full items-center justify-center bg-slate-100">
											<p class="text-gray-500">Cargando mapa...</p>
										</div>
									{/if}
								</div>

								{#if boat?.expand?.port}
									<div class="mt-3 rounded-lg bg-slate-50 p-3">
										<p class="font-medium text-gray-700">
											<Compass class="mr-1 inline-flex h-4 w-4" />
											{boat.expand.port.name}
										</p>
										<p class="text-sm text-gray-600">
											{boat.expand.port.province}, {boat.expand.port.country || 'España'}
										</p>
									</div>
								{/if}
							</Card.Content>
						</Card.Root>

						<!-- Sección de Reseñas -->
						<Card.Root id="reviews" class="mb-6">
							<Card.Header class="pb-2">
								<Card.Title class="flex items-center justify-between">
									<div class="flex items-center">
										<Star class="text-kbblue mr-2 h-5 w-5" />
										Reseñas
									</div>
									{#if reviews.length > 0}
										<div class="flex items-center gap-2 text-base font-normal">
											<Star class="h-5 w-5 fill-yellow-400 text-yellow-400" />
											<span class="font-semibold">{averageRating.toFixed(1)}</span>
											<span class="text-gray-500"
												>({reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'})</span
											>
										</div>
									{/if}
								</Card.Title>
							</Card.Header>
							<Card.Content class="px-4">
								{#if loadingReviews}
									<div class="flex items-center justify-center py-8">
										<p class="text-gray-500">Cargando reseñas...</p>
									</div>
								{:else if reviews.length === 0}
									<div class="rounded-lg bg-slate-50 p-8 text-center">
										<Star class="mx-auto mb-3 h-12 w-12 text-gray-300" />
										<p class="text-gray-600">Aún no hay reseñas para esta experiencia</p>
										<p class="mt-1 text-sm text-gray-500">Sé el primero en dejar una reseña</p>
									</div>
								{:else}
									<div class="space-y-4">
										{#each reviews as review (review.id)}
											<div class="rounded-lg border p-4">
												<!-- Información del reviewer -->
												<div class="mb-3 flex items-center gap-3">
													{#if review.expand?.reviewer}
														<img
															class="h-10 w-10 rounded-full object-cover"
															src={pb.files.getURL(
																review.expand.reviewer,
																review.expand.reviewer.avatar
															)}
															alt={review.expand.reviewer.username}
															width="40"
															height="40"
														/>
														<div class="flex-1">
															<p class="font-medium text-gray-900">
																@{review.expand.reviewer.username}
															</p>
															<p class="text-sm text-gray-500">
																{new Date(review.created).toLocaleDateString('es-ES', {
																	year: 'numeric',
																	month: 'long'
																})}
															</p>
														</div>
													{/if}
													<!-- Rating -->
													<div class="flex items-center gap-1">
														{#each Array(5) as _, i (i)}
															<Star
																class={`h-4 w-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
															/>
														{/each}
													</div>
												</div>

												<!-- Comentario -->
												{#if review.comment}
													<p class="mb-3 text-gray-700">{review.comment}</p>
												{/if}

												<!-- Fotos de la reseña -->
												{#if review.photos && review.photos.length > 0}
													<div class="flex gap-2 overflow-x-auto">
														{#each review.photos as photo, photoIndex (photo)}
															<button
																type="button"
																onclick={() => openReviewPhotosDialog(review, photoIndex)}
																class="focus:ring-kbblue shrink-0 overflow-hidden rounded-lg focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
															>
																<img
																	src={pb.files.getURL(review, photo, { thumb: '0x150' })}
																	alt="Foto de la reseña"
																	class="h-20 w-20 cursor-pointer rounded-lg object-cover transition-transform hover:scale-105"
																/>
															</button>
														{/each}
													</div>
												{/if}
											</div>
										{/each}
									</div>
								{/if}
							</Card.Content>
						</Card.Root>

						<ListingSeoSection intro={seoIntro} faqs={seoFaqs} />
					</div>

					<!-- Panel de reserva (desktop) -->
					<div class="col-span-4 mt-6 hidden md:block">
						<div class="sticky top-20 z-10 transition-all duration-200">
							<Card.Root class="border-2">
								<Card.Header class="pb-4">
									<div class="flex items-baseline justify-between">
										<Card.Title class="text-kbprimary text-4xl font-bold">
											{minListingPrice ?? '—'}€
											<span class="text-base font-normal text-gray-500">
												{#if listing?.pricingType === 'per_seat'}
													/ persona / día
												{:else}
													/ día
												{/if}
											</span>
										</Card.Title>
										{#if reviews.length > 0}
											<a
												href="#reviews"
												class="flex items-center gap-1.5 text-base text-gray-600 hover:text-gray-900"
											>
												<Star class="h-5 w-5 fill-yellow-400 text-yellow-400" />
												<span class="font-semibold">{averageRating.toFixed(1)}</span>
												<span class="text-gray-500">({reviews.length})</span>
											</a>
										{/if}
									</div>
								</Card.Header>

								<Card.Content class="space-y-4">
									<!-- Selector de fechas -->
									<div>
										<label class="text-sm font-medium text-gray-700">Selecciona fechas</label>
										{#if loadingAvailableDates}
											<p class="mt-1 text-xs text-gray-500">Cargando fechas disponibles...</p>
										{:else if availableDateRanges.length === 0}
											<p class="mt-1 text-xs text-red-500">
												No hay fechas disponibles para este barco
											</p>
										{/if}
										<div class="mt-1">
											<Popover.Root>
												<Popover.Trigger class="w-full">
													<div
														class={cn(
															buttonVariants({ variant: 'outline' }),
															'w-full justify-start border-slate-300 bg-white text-left font-normal hover:bg-slate-50'
														)}
													>
														<Calendar class="text-kbblue mr-2 h-5 w-5" />
														{#if valueDate && valueDate.start}
															{#if valueDate.end}
																{df.format(valueDate.start.toDate(getLocalTimeZone()))} - {df.format(
																	valueDate.end.toDate(getLocalTimeZone())
																)}
															{:else}
																{df.format(valueDate.start.toDate(getLocalTimeZone()))}
															{/if}
														{:else}
															Selecciona fechas
														{/if}
													</div>
												</Popover.Trigger>
												<Popover.Content class="w-auto p-0" align="center">
													<p class="text-xs text-slate-600 text-center mt-2">
														💡 Para un día, haz clic 2 veces en la misma fecha
													</p>
													<RangeCalendar
														class="rounded-lg border [--cell-size:--spacing(10)] xl:[--cell-size:--spacing(11)]"
														bind:value={valueDate}
														onStartValueChange={(v) => {
															startValue = v;
														}}
														numberOfMonths={2}
														weekStartsOn={1}
														placeholder={valueDate?.start}
														isDateDisabled={isDateDisabled}
														minDays={1}
													>
														{#snippet day({ day, outsideMonth })}
															<RangeCalendarDay
																class="flex h-(--cell-size) flex-col items-center justify-center gap-0 py-0.5 leading-none"
															>
																<span class="text-sm">{day.day}</span>
																{#if !outsideMonth && isDateAvailable(day) && getDateRangePriceForDay(day) !== null}
																	<span class="text-[8px] leading-none text-slate-500"
																		>{getDateRangePriceForDay(day) ?? 0}€</span
																	>
																{/if}
															</RangeCalendarDay>
														{/snippet}
													</RangeCalendar>
												</Popover.Content>
											</Popover.Root>
										</div>
									</div>

									<!-- Selector de tripulantes -->
									<div>
										<label class="text-sm font-medium text-gray-700">Número de tripulantes</label>
										<div class="relative mt-1">
											<button
												onclick={() => (chooseCreNumberOpen = !chooseCreNumberOpen)}
												type="button"
												class="focus:ring-kbblue flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm shadow-xs hover:bg-slate-50 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
											>
												<div class="flex items-center">
													<Users class="text-kbblue mr-2 h-5 w-5" />
													{selectedCrewNumber}
													{selectedCrewNumber === 1 ? 'Tripulante' : 'Tripulantes'}
												</div>
												<ChevronsUpDown class="h-4 w-4 text-gray-400" />
											</button>

											{#if chooseCreNumberOpen}
												<div
													class="ring-opacity-5 absolute right-0 z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-hidden"
												>
													{#each Array(listing.spots || 10) as _, i (i)}
														<button
															onclick={() => {
																selectedCrewNumber = i + 1;
																chooseCreNumberOpen = false;
															}}
															class={`block w-full px-4 py-2 text-left text-sm ${selectedCrewNumber === i + 1 ? 'bg-kbblue/10 text-kbblue' : 'text-gray-700 hover:bg-gray-100'}`}
														>
															{i + 1}
															{i + 1 === 1 ? 'Tripulante' : 'Tripulantes'}
														</button>
													{/each}
												</div>
											{/if}
										</div>
									</div>

									<Separator.Root />

									<!-- Resumen de costos -->
									<div class="rounded-lg bg-slate-50 p-3">
										<h4 class="mb-3 font-medium">Resumen del precio</h4>
										{#if priceBreakdownResult}
											<BookingPriceBreakdown
												lines={priceBreakdownResult.lines}
												total={priceBreakdownResult.total}
												compact
											/>
										{:else}
											<p class="text-sm text-slate-600">
												Selecciona fechas disponibles para ver el precio.
											</p>
										{/if}
									</div>

									<!-- Botón de reserva -->
									<Button
										class="w-full py-2.5 font-medium"
										onclick={handleBookingCreation}
										disabled={loadingAvailableDates || availableDateRanges.length === 0}
									>
										<CreditCard class="mr-2 h-5 w-5 shrink-0" />
										<span class="truncate">
											{#if loadingAvailableDates}
												Cargando...
											{:else if availableDateRanges.length === 0}
												No disponible
											{:else}
												Reservar ahora
											{/if}
										</span>
									</Button>

									<a
										class={cn(
											buttonVariants({ variant: 'default' }),
											'mt-2 flex w-full items-center justify-center gap-2 bg-[#25D366] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1DA851] active:bg-[#1A9D47]'
										)}
										href={whatsappUrl}
										target="_blank"
										rel="noreferrer"
									>
										<WhatsAppIcon class="h-4 w-4 shrink-0" size={16} />
										<span class="truncate text-center">¿No lo tienes claro? Escribenos!</span>
									</a>

									<!-- Garantías -->
									<div class="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
										<div class="flex items-center gap-2">
											<Shield class="text-kbblue h-4 w-4" />
											<p>Pago seguro</p>
										</div>
										<div class="mt-1 flex items-center gap-2">
											<Calendar class="text-kbblue h-4 w-4" />
											<p>Cancelación flexible disponible</p>
										</div>
									</div>
								</Card.Content>
							</Card.Root>

							<!-- Reportar anuncio -->
							<div class="mt-4 text-center">
								<a
									href={localizeHref(`/reportar-anuncio?id=${listing?.id}`)}
									class="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
								>
									<Flag class="h-4 w-4" /> Reportar este anuncio
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{:else}
	<div class="flex h-screen items-center justify-center">
		<div class="text-center">
			<div class="mb-4 flex justify-center">
				<ShipWheel class="text-kbblue h-10 w-10 animate-spin" />
			</div>
			<p class="text-gray-600">Cargando información del barco...</p>
		</div>
	</div>
{/if}
